import {
  Alert,
  AlertDescription,
  AlertIcon,
  ButtonGroup,
  Card,
  Center,
  Flex,
  Grid,
  Link,
  Text,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link as RouterLink, Outlet } from "react-router";

import { Restricted } from "@/app/components/Restricted";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import FormReset from "@/common/components/Form/FormReset";
import FormSubmit from "@/common/components/Form/FormSubmit";
import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import { RuleSimpleCompanySelector } from "@/common/components/Rule/RuleSimpleCompanySelector";
import { RuleSimpleUserSelector } from "@/common/components/Rule/RuleSimpleUserSelector";
import { SectionHeading } from "@/common/components/SectionHeading";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { useSlugParam } from "@/common/hooks/useParam";
import { useFeatureData } from "@/feature/data/useFeatureData";
import { useFeatureOptInMembershipsData } from "@/feature/data/useFeatureOptInMembershipsData";
import { useFeatureOptInMembershipsUpdateMutation } from "@/feature/data/useFeatureOptInMembershipsUpdateMutation";
import { useFeatureTargetingData } from "@/feature/data/useFeatureTargetingData";
import { useFlag } from "@reflag/react-sdk";
import {
  FeatureOptInMemberships,
  FeatureOptInMembershipsSchema,
} from "@reflag/shared/featureTargetingAPI";

import FeatureSideBarWrapper from "../components/FeatureSidebarWrapper";

function OptInMembershipSelector({
  type,
  ids,
  isReadOnly,
  onChange,
}: {
  type: "user" | "company";
  ids?: string[];
  isReadOnly: boolean;
  onChange: (ids: string[]) => void;
}) {
  const emptyText = type === "user" ? "No users" : "No companies";

  if (isReadOnly && (!ids || ids.length === 0)) {
    return (
      <Text color="dimmed" fontSize="sm">
        {emptyText}
      </Text>
    );
  }

  if (type === "user") {
    return (
      <RuleSimpleUserSelector
        allowReadOnlyOpen
        isReadOnly={isReadOnly}
        summaryCount={10}
        value={ids}
        onChange={onChange}
      />
    );
  }

  return (
    <RuleSimpleCompanySelector
      allowReadOnlyOpen
      isReadOnly={isReadOnly}
      summaryCount={10}
      value={ids}
      onChange={onChange}
    />
  );
}

function FeatureOptInForm({
  featureId,
  optInEnabled,
  isArchived,
}: {
  featureId: string;
  optInEnabled: boolean;
  isArchived: boolean;
}) {
  const { env, userHasRole } = useEnvironment();
  const { data: memberships, isLoading } =
    useFeatureOptInMembershipsData(featureId);
  const { data: targeting = [] } = useFeatureTargetingData(featureId);
  const updateMembershipsMutation =
    useFeatureOptInMembershipsUpdateMutation(featureId);
  const { isEnabled: enforceUserPermissions } = useFlag("user-permissions");
  const isContributor = userHasRole("contributor") || !enforceUserPermissions;
  const isPublisher = userHasRole("publisher") || !enforceUserPermissions;
  const canEdit =
    !isArchived &&
    ((env.isProduction && isPublisher) || (!env.isProduction && isContributor));
  const currentEnvironmentTargeting = targeting.find(
    (version) => version.environment.id === env.id,
  );
  const optInsAreInactive =
    optInEnabled && currentEnvironmentTargeting?.targetingMode === "none";

  const form = useForm<FeatureOptInMemberships>({
    resolver: zodResolver(FeatureOptInMembershipsSchema),
    defaultValues: {
      userIds: [],
      companyIds: [],
    },
  });

  useEffect(() => {
    if (!memberships || form.formState.isDirty) {
      return;
    }

    form.reset(memberships);
  }, [form, form.formState.isDirty, memberships]);

  const onSubmit = useFormMutationSubmitHandler(
    form,
    updateMembershipsMutation,
    (updatedMemberships) => {
      form.reset(updatedMemberships);
    },
    {
      successToast: "Opt-ins updated",
      errorToast: "Failed to update opt-ins",
    },
  );

  if (isLoading) {
    return (
      <Center flexGrow={1}>
        <DelayedSpinner />
      </Center>
    );
  }

  return (
    <FormProvider {...form}>
      <Flex as="form" direction="column" gap={4} w="full" onSubmit={onSubmit}>
        <SectionHeading>Opt-ins</SectionHeading>
        <Text color="dimmed" fontSize="sm" mt={-2}>
          Enable/disable opting in under{" "}
          <Link as={RouterLink} to="../settings/opt-in">
            Settings
          </Link>
          .
        </Text>
        {optInsAreInactive && (
          <Alert status="warning">
            <AlertIcon />
            <AlertDescription>
              Opt-ins are enabled, but Access is set to No one in {env.name}.
              Opted-in users and companies won't get access until Access is set
              to Some or Everyone.
            </AlertDescription>
          </Alert>
        )}
        {!optInEnabled && (
          <Text color="dimmed" fontSize="sm">
            End-user opt-in is currently disabled. Existing stored memberships
            can be removed, but new opt-ins cannot be added until opt-in is
            enabled in Settings.
          </Text>
        )}

        <Card p={4} w="full">
          <Grid gap={2} templateColumns="80px auto">
            <Text fontSize="sm" lineHeight={8}>
              Companies
            </Text>
            <ManagedFormControl
              name="companyIds"
              render={({ field }) => (
                <OptInMembershipSelector
                  ids={field.value}
                  isReadOnly={!canEdit}
                  type="company"
                  onChange={field.onChange}
                />
              )}
            />
            <Text fontSize="sm" lineHeight={8}>
              Users
            </Text>
            <ManagedFormControl
              name="userIds"
              render={({ field }) => (
                <OptInMembershipSelector
                  ids={field.value}
                  isReadOnly={!canEdit}
                  type="user"
                  onChange={field.onChange}
                />
              )}
            />
          </Grid>
        </Card>

        {canEdit && (
          <ButtonGroup>
            <FormSubmit
              isLoading={updateMembershipsMutation.isPending}
              disableOnPristine
              showTooltipOnPristine
            />
            <FormReset />
          </ButtonGroup>
        )}
      </Flex>
    </FormProvider>
  );
}

export default function FeatureOptIn() {
  const { featureId } = useSlugParam("feature");
  const { data: feature } = useFeatureData(featureId!);

  return (
    <FeatureSideBarWrapper>
      <Flex direction="column" flexGrow={1} h="full" px={6} py={4}>
        <Restricted
          alertStyle="callout"
          displayContents="normal"
          feature={feature}
          role="contributor"
        >
          {feature && (
            <FeatureOptInForm
              featureId={feature.id}
              isArchived={feature.isArchived}
              optInEnabled={feature.optInEnabled}
            />
          )}
        </Restricted>
        <Outlet />
      </Flex>
    </FeatureSideBarWrapper>
  );
}
